/**
 * The chapter's lessons as a winding path (mockup 03,
 * [D-140](../../../docs/DECISIONS.md#d-140)).
 *
 * A path rather than a menu: one step is glowing, everything behind it is
 * gold, everything ahead is waiting. The child stands on the step to play
 * next, wearing what they chose, with their pet — or the egg they are
 * hatching — beside them.
 *
 * Every node is a real button with the lesson's title and state in its name,
 * so a screen reader walks the path in order, the same as a finger does.
 */

import '@capytalk/tokens/blocks/path.css';
import { avatarSvg } from '@capytalk/tokens';
import { windingHeight, windingLayout, trail } from './winding.js';

export interface WindingLesson {
  readonly lessonId: string;
  readonly titleL1: string;
  readonly state: 'done' | 'now' | 'locked';
  /** Already worded for a child: "about 4 min". */
  readonly length: string;
}

export interface Standing {
  readonly species: string;
  readonly background: string;
  readonly worn: Record<string, { itemId: string; dye: string | null } | null>;
}

/** Who keeps them company: a pet, or the egg they are hatching. */
export interface Companion {
  readonly emoji: string;
  readonly name: string;
}

const MARK = { done: '⭐', now: '▶', locked: '🔒' } as const;
const SAID = { done: 'done', now: 'play next', locked: 'not yet' } as const;

export default function Winding({
  lessons,
  standing,
  companion,
  onPlay,
}: {
  readonly lessons: readonly WindingLesson[];
  readonly standing: Standing | null;
  readonly companion: Companion | null;
  readonly onPlay: (lessonId: string) => void;
}) {
  const placed = windingLayout(lessons.map((lesson) => lesson.state));
  const at = (x: number, size: number) => `calc(${String(x)}% - ${String(size / 2)}px)`;

  return (
    <div className="path" style={{ height: windingHeight(lessons.length) }}>
      {placed.slice(1).flatMap((to, i) => {
        const from = placed[i];
        if (from === undefined) return [];
        return trail(from, to).map((dot, k) => (
          <div
            key={`${String(i)}.${String(k)}`}
            className="dotline"
            aria-hidden="true"
            style={{ left: at(dot.x, 6), top: dot.y - 3 }}
          />
        ));
      })}

      {lessons.map((lesson, i) => {
        const spot = placed[i];
        /* c8 ignore next -- one place per lesson, by construction */
        if (spot === undefined) return null;
        const locked = lesson.state === 'locked';
        const now = lesson.state === 'now';
        return (
          <div key={lesson.lessonId}>
            {now && standing !== null && (
              <svg
                viewBox="0 0 160 240"
                width={48}
                height={72}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: at(spot.x, 48),
                  top: spot.y - 64,
                  filter: 'drop-shadow(0 4px 3px rgba(0,0,0,.22))',
                  pointerEvents: 'none',
                }}
                // Local, generated, CI-checked markup — never anything a response chose.
                dangerouslySetInnerHTML={{
                  // No card behind them: on the path they stand on the step itself.
                  __html: avatarSvg({
                    species: standing.species,
                    background: 'none',
                    loadout: standing.worn,
                    uid: 'path',
                  }),
                }}
              />
            )}
            {now && companion !== null && (
              <div
                role="img"
                aria-label={`${companion.name} is with you`}
                style={{
                  position: 'absolute',
                  left: `calc(${String(spot.x)}% + ${String(spot.size / 2 - 4)}px)`,
                  top: spot.y - 16,
                  fontSize: 26,
                  filter: 'drop-shadow(0 3px 2px rgba(0,0,0,.2))',
                  pointerEvents: 'none',
                }}
              >
                {companion.emoji}
              </div>
            )}
            <button
              type="button"
              className={`node ${locked ? 'lock' : lesson.state}`}
              disabled={locked}
              aria-disabled={locked}
              aria-label={`${lesson.titleL1}, ${lesson.length}, ${SAID[lesson.state]}`}
              style={{
                left: at(spot.x, spot.size),
                top: spot.y,
                cursor: locked ? 'default' : 'pointer',
                fontSize: now ? 30 : 24,
                color: '#fff',
              }}
              onClick={() => {
                onPlay(lesson.lessonId);
              }}
            >
              <span aria-hidden="true">{MARK[lesson.state]}</span>
              {now && <span className="ring" aria-hidden="true" />}
            </button>
            <div
              className="nlabel"
              aria-hidden="true"
              style={{ left: at(spot.x, 110), top: spot.y + spot.size + 7 }}
            >
              {i + 1} · {lesson.titleL1}
            </div>
            {now && (
              <div
                className="startpip"
                aria-hidden="true"
                style={{ left: at(spot.x, 46), top: spot.y + spot.size + 28 }}
              >
                START
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
